import React from "react";
import InlineLoader from "../common/InlineLoader";
import EmptyState from "../common/EmptyState";
import "./KnowYourConstitution.scss";

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

// YouTube shorts / watch / youtu.be links ko embeddable URL me convert karta hai
const ARTICLE_PREFIX = /^\s*articles?\b\.?\s*/i;

const articleTokens = (articleNo) => {
  const body = String(articleNo || "").replace(ARTICLE_PREFIX, "");
  const tokens = [];
  const re = /(\d+)\s*([A-Za-z]{0,3})(?![A-Za-z])/g;
  let m;
  while ((m = re.exec(body))) {
    tokens.push({ num: parseInt(m[1], 10), suffix: m[2].toUpperCase() });
  }
  return tokens;
};

const compareSuffix = (a, b) => {
  if (a === b) return 0;
  if (a.length !== b.length) return a.length - b.length;
  return a < b ? -1 : 1;
};

const compareArticles = (x, y) => {
  const a = articleTokens(x.ArticleNo);
  const b = articleTokens(y.ArticleNo);

  if (!a.length && !b.length) {
    return String(x.ArticleNo || "").localeCompare(String(y.ArticleNo || ""));
  }
  if (!a.length) return 1;
  if (!b.length) return -1;

  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i].num !== b[i].num) return a[i].num - b[i].num;
    const sc = compareSuffix(a[i].suffix, b[i].suffix);
    if (sc !== 0) return sc;
  }
  if (a.length !== b.length) return a.length - b.length;
  return (x.id || 0) - (y.id || 0);
};

const sortArticles = (list) => [...(list || [])].sort(compareArticles);

const articleLabel = (articleNo) => {
  const no = String(articleNo || "").trim();
  return ARTICLE_PREFIX.test(no) ? no : `Article ${no}`;
};

const getEmbedUrl = (url = "") => {
  const m = String(url).match(/(?:shorts\/|v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
};

// ------------------------------------------------------------------
// Summary string ko 2 hisson me todta hai. Dono heading ke KAI naam ho sakte hain,
// aur jo naam API ne bheja wahi UI par dikhta hai (case se fark nahi padta, CSS
// usse capitals me dikhata hai):
//
//   1st block  (highlighted "official" style):
//       OFFICIAL TEXT
//       CONSTITUTIONAL PROVISION
//       CONSTITUTIONAL PROVISION (REPHRASED)
//   2nd block  (highlighted "simple" style):
//       SIMPLIFIED EXPLANATION
//       SIMPLE EXPLANATION
//
// "CONSTITUTIONAL PROVISION" / "SIMPLE EXPLANATION" normal sentence me bhi aa
// sakte hain, isliye ye tabhi heading maane jaate hain jab unke baad "-", "–", "—",
// ":" ya nayi line ho — ya wo poore CAPITALS me likhe ho.
// ------------------------------------------------------------------
const SEP = "(?=\\s*(?:[-–—:]|\\n|$))";

const OFFICIAL_MARKERS = [
  /OFFICIAL\s+TEXT/i,
  new RegExp("CONSTITUTIONAL\\s+PROVISION(?:\\s*\\(\\s*REPHRASED\\s*\\))?" + SEP, "i"),
  /CONSTITUTIONAL\s+PROVISION(?:\s*\(\s*REPHRASED\s*\))?/, // CAPITALS, bina separator
];

const SIMPLE_MARKERS = [
  /SIMPLIFIED\s+EXPLANATION/i,
  new RegExp("SIMPLE\\s+EXPLANATION" + SEP, "i"),
  /SIMPLE\s+EXPLANATION/, // CAPITALS, bina separator
];

// sabse pehle aane wala marker (barabar ho to lamba wala)
const findMarker = (text, patterns) => {
  let best = null;
  patterns.forEach((re) => {
    const m = re.exec(text);
    if (!m) return;
    if (!best || m.index < best.index || (m.index === best.index && m[0].length > best.length)) {
      best = { index: m.index, length: m[0].length, label: m[0].replace(/\s+/g, " ").trim() };
    }
  });
  return best;
};

const parseSummary = (raw) => {
  const text = String(raw || "");

  const off = findMarker(text, OFFICIAL_MARKERS);
  const sim = findMarker(text, SIMPLE_MARKERS);

  if (!off && !sim) {
    return {
      plain: text.trim(),
      official: "",
      simplified: "",
      officialLabel: "Official Text",
      simplifiedLabel: "Simplified Explanation",
    };
  }

  // marker ke baad aane wale "-", "–", "—", ":" hata do
  const clean = (s) => s.replace(/^\s*[-–—:]\s*/, "").trim();

  const official = off
    ? clean(text.slice(off.index + off.length, sim && sim.index > off.index ? sim.index : undefined))
    : "";

  const simplified = sim
    ? clean(text.slice(sim.index + sim.length, off && off.index > sim.index ? off.index : undefined))
    : "";

  return {
    plain: "",
    official,
    simplified,
    officialLabel: off ? off.label : "Official Text",
    simplifiedLabel: sim ? sim.label : "Simplified Explanation",
  };
};

// Text ko lines me todta hai:
//  - new line pe split
//  - (1) (2) (3) numbered clauses alag line me
const toLines = (text) =>
  String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .flatMap((l) => l.split(/\s+(?=\(\d+\)\s)/))
    .map((l) => l.trim())
    .filter(Boolean);

// Ek formatted block: heading + andar ki lines (label: body pattern bold hota hai)
const SummaryBlock = ({ variant, icon, title, text }) => {
  if (!text) return null;

  const lines = toLines(text);
  let note = null;

  // "PLAIN-LANGUAGE RESTATEMENT (not verbatim)" ko badge banao
  if (lines.length && /^PLAIN-LANGUAGE/i.test(lines[0])) {
    note = lines.shift();
  }

  return (
    <div className={`const-sum-block ${variant}`}>
      <div className="const-sum-head">
        <span className="const-sum-title" style={{ maxWidth: "100%", lineHeight: 1.35 }}>
          <i className={`ti ${icon}`}></i> {title}
        </span>
        {note && <span className="const-sum-note">{note}</span>}
      </div>

      <div className="const-sum-body">
        {lines.map((line, i) => {
          const numbered = line.match(/^(\(\d+\))\s+(.*)$/s);
          if (numbered) {
            return (
              <p className="const-sum-line clause" key={i}>
                <span className="const-clause-no">{numbered[1]}</span>
                <span>{numbered[2]}</span>
              </p>
            );
          }

          const labeled = line.match(/^([A-Z][^:.]{2,70}):\s+(.+)$/s);
          if (labeled) {
            return (
              <p className="const-sum-line" key={i}>
                <strong className="const-sum-label">{labeled[1]}:</strong> {labeled[2]}
              </p>
            );
          }

          return (
            <p className="const-sum-line" key={i}>
              {line}
            </p>
          );
        })}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

const KnowYourConstitution = ({
  constParts,
  constPartsLoading,
  selectedPartId,
  setSelectedPartId,
  constArticles,
  constArticlesLoading,
  selectedPart,
  constOpenVideo,
  toggleConstVideo,
}) => {
  const sortedArticles = React.useMemo(() => sortArticles(constArticles), [constArticles]);

  return (
    <div className="const-container">
      <div className="pub-section-lbl" style={{ color: "#12308c", fontWeight: 700, paddingTop: 16 }}>
        <i className="ti ti-scale"></i> Know Your Constitution
      </div>
      <div className="const-card">
        <p className="ctf-intro">
          All Parts of the Constitution, the Articles that actually matter within each, explained plainly — with mentor video walkthroughs where available.
        </p>
        <div className="const-part-row">
          {constPartsLoading && <InlineLoader label="Loading parts…" />}
          {!constPartsLoading && constParts.map((p) => (
            <button
              key={p.id}
              className={`const-part-chip${selectedPartId === p.id ? " on" : ""}`}
              onClick={() => setSelectedPartId(p.id)}
            >
              {p.PartNo}
            </button>
          ))}
        </div>
        <div className="const-part-body">
          {selectedPart && (
            <>
              <div className="const-part-title">{selectedPart.PartTitle}</div>
              <div className="const-part-sub">{selectedPart.PartNo}</div>

              {constArticlesLoading && <InlineLoader label="Loading articles…" />}

              {!constArticlesLoading && sortedArticles.length === 0 && (
                <EmptyState label={`No articles found for ${selectedPart.PartTitle}.`} />
              )}

              {!constArticlesLoading && sortedArticles.map((a) => {
                const vid = `cvid-${a.id}`;
                const hasVideo = !!a.ShortVideo;
                const { plain, official, simplified, officialLabel, simplifiedLabel } = parseSummary(a.ShortSummary);

                return (
                  <div className="const-article-card" key={vid}>
                    <div className="const-article-num">{articleLabel(a.ArticleNo)}</div>
                    <div className="const-article-name">{a.Title}</div>

                    <div className="const-article-txt">
                      {plain && <p className="const-sum-line">{plain}</p>}

                      <SummaryBlock
                        variant="official"
                        icon="ti-gavel"
                        title={officialLabel}
                        text={official}
                      />

                      <SummaryBlock
                        variant="simple"
                        icon="ti-bulb"
                        title={simplifiedLabel}
                        text={simplified}
                      />
                    </div>

                    {hasVideo && (
                      <>
                        <button className="const-video-btn" onClick={() => toggleConstVideo(vid)}>
                          <i className="ti ti-player-play"></i> Mentor Video / Shorts on this Article
                        </button>
                        <div className={`const-video-placeholder${constOpenVideo[vid] ? " open" : ""}`}>
                          {constOpenVideo[vid] ? (
                            <iframe
                              width="100%"
                              height="100%"
                              src={getEmbedUrl(a.ShortVideo)}
                              title={a.Title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{ borderRadius: 8 }}
                            ></iframe>
                          ) : (
                            <>
                              <i className="ti ti-video big"></i>
                              <span>Mentor's recorded explainer for {articleLabel(a.ArticleNo)} will appear here once uploaded — a 60–90 second video walkthrough, same depth as the written summary above.</span>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="ec-src">
          Article text paraphrased for brevity — always refer to the original Article for precise legal wording. Not a substitute for legal advice.
        </div>
      </div>
    </div>
  );
};

export default KnowYourConstitution;